import {
  Button,
  Grid,
  Paper,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { Fragment } from 'react';

// design system
import { colors } from 'design-system';

// models
import QuotationsMeasurements from 'src/models/Chart/QuotationsMeasurements';

// icons
import {
  CameraIcon,
  DocumentIcon,
  EqualsIcons,
  PlusIcon,
  ShieldIcon,
} from 'src/components/Icons';

// constants
import {
  QuotationStatusMetric,
  TOTAL_CREATED_QUOTATIONS,
  TOTAL_EMITTED_POLICIES,
  TOTAL_PENDING_EMISSIONS,
  TOTAL_PENDING_INSPECTIONS,
} from 'src/constants/metricType';

const useStyle = makeStyles(() =>
  createStyles({
    quotationStatusCard: {
      padding: '31px 20px',
    },
    titleCard: {
      fontWeight: 900,
      fontSize: '19px',
      lineHeight: '24px',
    },
    titleAmountQuotation: {
      fontWeight: 700,
      fontSize: '36px',
      lineHeight: '45px',
    },
    containerMiddleIcons: {
      display: 'flex',
      alignItems: 'center',
      margin: '0px 20px',
    },
    subtitle: {
      fontWeight: 700,
      fontSize: '15px',
    },
    buttonSeeDetails: {
      boxShadow: 'none',
      color: 'white',
      background: colors.bright_turquoise,
      borderRadius: '80px',
      '&:hover': {
        background: colors.bright_turquoise,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
      },
      '&:active': {
        background: colors.assa_blue,
      },
    },
  }),
);

type CardContentQuotationsStatusType = {
  title: string;
  icon: JSX.Element;
  status: QuotationStatusMetric;
};

const cardContentQuotationsStatus: CardContentQuotationsStatusType[] = [
  {
    title: 'Por inspección',
    icon: <CameraIcon />,
    status: TOTAL_PENDING_INSPECTIONS,
  },
  {
    title: 'Por emisión',
    icon: <DocumentIcon />,
    status: TOTAL_PENDING_EMISSIONS,
  },
  {
    title: 'Pólizas',
    icon: <ShieldIcon />,
    status: TOTAL_EMITTED_POLICIES,
  },
];

type CardsWithQuotationsByStatusProps = {
  listQuotationsByStatus: QuotationsMeasurements[];
  handleOnClickSeeDetails: (
    quotationStatusMetric: QuotationStatusMetric,
  ) => void;
};

const CardsWithQuotationsByStatus = ({
  listQuotationsByStatus,
  handleOnClickSeeDetails,
}: CardsWithQuotationsByStatusProps): JSX.Element => {
  const classes = useStyle();
  const totalCreatedQuoations = listQuotationsByStatus.find(
    ({ type }: { type: string }) => type === TOTAL_CREATED_QUOTATIONS,
  );

  return (
    <Grid
      item
      sm={12}
      md={10}
      container
      justifyContent="center"
      alignItems="center"
      style={{ gap: 16 }}
    >
      <Grid item style={{ padding: 20, alignSelf: 'flex-start' }}>
        <Grid container direction="column">
          <Grid item>
            <Typography>Cotizaciones</Typography>
            <Typography>creadas</Typography>
          </Grid>
          <Grid item container alignItems="center" style={{ gap: 8 }}>
            <DocumentIcon />
            <Typography className={classes.titleAmountQuotation}>
              {totalCreatedQuoations?.value}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <EqualsIcons />
      </Grid>
      {cardContentQuotationsStatus.map(({ title, icon, status }, index) => {
        const quotationsByStatus = listQuotationsByStatus.find(
          ({ type }: { type: string }) => type === status,
        );
        return (
          <Fragment key={status}>
            {/* don't show plus icon if is first element */}
            {index !== 0 && (
              <Grid item data-testid={`plus-icons-${index}`}>
                <PlusIcon />
              </Grid>
            )}
            <Grid item xs sm>
              <Paper
                style={{
                  padding: 20,
                  boxShadow: '0px 5px 5px rgb(0 0 0 / 8%)',
                  borderRadius: '10px',
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                  style={{ gap: 8 }}
                >
                  <Typography
                    align="center"
                    data-testid={`card-title-${status}`}
                  >
                    {title}
                  </Typography>
                  <Grid
                    item
                    container
                    justifyContent="center"
                    alignItems="center"
                    style={{ gap: 8 }}
                  >
                    {icon}
                    <Typography
                      className={classes.titleAmountQuotation}
                      data-testid={`card-value-${status}`}
                    >
                      {quotationsByStatus?.value}
                    </Typography>
                  </Grid>
                  <Button
                    data-testid={`button-${status}`}
                    variant="contained"
                    className={classes.buttonSeeDetails}
                    onClick={() => handleOnClickSeeDetails(status)}
                  >
                    Ver detalle
                  </Button>
                </Grid>
              </Paper>
            </Grid>
          </Fragment>
        );
      })}
    </Grid>
  );
};

export default CardsWithQuotationsByStatus;
